import { useDownloadFile } from '@hooks/useDownloadFile';
import FilePreviewDialog from '@components/InputDocuments/FilePreviewDialog';
import {
    Button as MuiButton,
    ButtonBase as MaterialButton,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Document, Page } from 'react-pdf';
import CVTemplateClassic from './CvTemplateClassic';
import { ITalent, ProfessionList } from './cv-generator.types';
import usePdfWorker from './use-pdf-worker';

type ResumeDownloadFile = {
    _id?: string;
    name: string;
    url?: string;
    context?: string;
};

type CvGenerationModalProps = {
    isOpen: boolean;
    closeModal: () => void;
    professionList: ProfessionList;
    userData: ITalent;
};

type CvTemplateOption = {
    key: string;
    image: string;
    ready: boolean;
};

const TEMPLATE_OPTIONS: CvTemplateOption[] = [
    {
        key: '2',
        image: '/images/cv-template-02.jpg',
        ready: false,
    },
    {
        key: '1',
        image: '/images/cv-template-01.jpg',
        ready: true,
    },
    {
        key: '3',
        image: '/images/cv-template-03.jpg',
        ready: false,
    },
];

const PREVIEW_PAGE_WIDTH = 600;

const getHardSkills = (userData: ITalent, professionList: ProfessionList): string[] => {
    if (!Array.isArray(userData?.professions) || !Array.isArray(professionList?.professions)) {
        return [];
    }

    const selectedProfessionIds = new Set<string>([userData.principalProfession, ...userData.professions].filter(Boolean));
    const selectedMajorIds = new Set<string>(userData.majors || []);

    const relatedProfessions = professionList.professions.filter(({ _id }) => selectedProfessionIds.has(_id));

    const specialtyNames = relatedProfessions
        .flatMap(({ specialties }) => specialties)
        .filter(({ _id }) => selectedMajorIds.has(_id))
        .map(({ specialtyName }) => specialtyName)
        .filter(Boolean);

    return Array.from(new Set(specialtyNames));
};

const CvGenerationModal = ({ isOpen, closeModal, userData, professionList }: CvGenerationModalProps) => {
    const [step, setStep] = useState(0);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [resumePreviewOpen, setResumePreviewOpen] = useState(false);
    const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
    const [resumePreviewPages, setResumePreviewPages] = useState(0);

    const workerReady = usePdfWorker();
    const hardSkills = useMemo(() => getHardSkills(userData, professionList), [professionList, userData]);
    const resume = userData?.resume;
    const resumeFile = useMemo<ResumeDownloadFile | null>(
        () => ((userData?.files as ResumeDownloadFile[] | undefined)?.find(file => file?.context === 'resume') ?? null),
        [userData?.files]
    );
    const hasUploadedResume = Boolean(resumeFile?._id || resumeFile?.url || resume?.path);

    const { download: downloadResume, downloadFromUrl, loading: isResumeDownloadLoading } = useDownloadFile({
        onSuccess: url => {
            setResumePreviewUrl(url);
            setResumePreviewOpen(true);
        },
        onError: error => {
            console.error('Resume download failed:', error);
        },
    });

    useEffect(() => {
        if (!isOpen || step !== 1 || !workerReady) {
            return;
        }

        let isActive = true;
        let generatedUrl: string | null = null;

        const generatePdf = async () => {
            setIsLoading(true);
            setNumPages(0);

            try {
                const blob = await pdf(<CVTemplateClassic userData={userData} hardSkills={hardSkills} />).toBlob();
                if (!isActive) {
                    return;
                }

                generatedUrl = URL.createObjectURL(blob);
                setPdfUrl(previousUrl => {
                    if (previousUrl) {
                        URL.revokeObjectURL(previousUrl);
                    }

                    return generatedUrl;
                });
            } catch (error) {
                console.error('PDF generation failed:', error);
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        };

        generatePdf();

        return () => {
            isActive = false;

            if (generatedUrl) {
                URL.revokeObjectURL(generatedUrl);
            }
        };
    }, [hardSkills, isOpen, step, userData, workerReady]);

    const onClose = () => {
        setStep(0);
        setIsLoading(true);
        setNumPages(0);
        setResumePreviewOpen(false);
        setResumePreviewUrl(null);
        setResumePreviewPages(0);
        setPdfUrl(previousUrl => {
            if (previousUrl) {
                URL.revokeObjectURL(previousUrl);
            }

            return null;
        });
        closeModal();
    };

    const renderTemplates = () => {
        const renderComingSoon = (ready: boolean) => !ready && <div style={styles.comingSoonText}>Coming Soon</div>;
        const renderTemplate = ({ key, image, ready }: CvTemplateOption) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                    style={{
                        backgroundImage: `url(${image})`,
                        ...styles.templateOptionImage,
                        ...(ready && styles.selectedTemplateImage),
                        ...(!ready && styles.comingSoonTemplateImage),
                    }}
                />
                {renderComingSoon(ready)}
            </div>
        );

        return <div style={styles.templatesContainer}>{TEMPLATE_OPTIONS.map(renderTemplate)}</div>;
    };

    const renderCloseButton = () => (
        <MaterialButton style={styles.closeButtonStyle} onClick={onClose}>
            <i className="fi fi-br-cross" style={{ fontSize: '16px' }} />
        </MaterialButton>
    );
    const renderPurpleButton = (text: string, onPress: () => void) => (
        <div style={styles.actionButtonContainer}>
            <MuiButton variant="contained" color="primary" size="large" onClick={onPress}>
                {text}
            </MuiButton>
        </div>
    );

    const handleResumeDownload = () => {
        if (isResumeDownloadLoading) {
            return;
        }

        if (resumeFile?._id) {
            downloadResume(resumeFile);
            return;
        }

        const resumeUrl = resumeFile?.url || resume?.path;
        if (/^https?:\/\//i.test(resumeUrl || '')) {
            setResumePreviewUrl(resumeUrl as string);
            setResumePreviewOpen(true);
            return;
        }
    };

    const handleResumePreviewDownload = () => {
        const fileName = resumeFile?.name || resume?.name || 'resume';

        if (resumePreviewUrl) {
            downloadFromUrl(resumePreviewUrl, fileName);
            return;
        }

        if (resumeFile?._id) {
            downloadResume(resumeFile);
        }
    };

    const handleCloseResumePreview = () => {
        setResumePreviewOpen(false);
        setResumePreviewUrl(null);
        setResumePreviewPages(0);
    };

    const renderResumeButton = () => (
        <div style={styles.actionButtonContainer}>
            <MuiButton
                variant="outlined"
                size="large"
                onClick={handleResumeDownload}
                disabled={!hasUploadedResume || isResumeDownloadLoading}
                color="secondary">
                {isResumeDownloadLoading ? 'Téléchargement...' : `Télécharger le CV (${resumeFile?.name || resume?.name || 'CV'})`}
            </MuiButton>
        </div>
    );

    const renderInitialStep = () => (
        <div style={styles.initializeSetupContainer}>
            {renderCloseButton()}
            <Typography sx={styles.title}>Choisissez votre modèle de CV</Typography>
            {renderTemplates()}
            <div style={styles.actionsContainer}>
                {renderPurpleButton('Générer un CV', () => setStep(1))}
                <Typography>
                    ou
                </Typography>
                {renderResumeButton()}
            </div>
        </div>
    );
    const renderLoading = () => (
        <div style={styles.loadingContainer}>
            <CircularProgress size={100} style={{ marginBottom: '24px' }} />
            <Typography sx={styles.title}>Création de votre CV...</Typography>
        </div>
    );
    const renderPDFPreview = () => (
        <div style={styles.pdfPreviewContainer}>
            <Document
                file={pdfUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(error) => console.error('Error loading PDF:', error.message)}
            >
                {Array.from({ length: numPages }, (_, index) => (
                    <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={PREVIEW_PAGE_WIDTH}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                ))}
            </Document>
        </div>
    );

    const handleDownload = () => {
        if (!pdfUrl) {
            return;
        }

        const fileName = (userData?.fullName || 'talent').substring(0, 20).replace(/\s+/g, '_');
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${fileName}_CV.pdf`;
        link.click();
        onClose();
    };

    const renderFinalStep = () => (
        <div>
            {isLoading || !pdfUrl ? renderLoading() : renderPDFPreview()}
        </div>
    );

    const renderers = [renderInitialStep, renderFinalStep];

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
                {step === 1 && (
                    <DialogTitle
                        sx={{
                            zIndex: 1,
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        }}>
                        Aperçu du document
                    </DialogTitle>
                )}
                <DialogContent>
                    {renderers[step]()}
                </DialogContent>

                {step === 1 && (
                    <DialogActions
                        sx={{
                            zIndex: 1,
                            boxShadow: '0px -4px 12px rgba(0, 0, 0, 0.1)',
                        }}>
                        <MuiButton onClick={onClose} variant="text" color="secondary">
                            Annuler
                        </MuiButton>
                        <MuiButton
                            startIcon={<i className="fi fi-br-download" style={{ fontSize: '16px' }} />}
                            onClick={handleDownload}
                            color="primary"
                            disabled={isLoading || !pdfUrl}>
                            Télécharger
                        </MuiButton>
                    </DialogActions>
                )}
            </Dialog>

            {resumePreviewUrl && (
                <FilePreviewDialog
                    open={resumePreviewOpen}
                    onClose={handleCloseResumePreview}
                    file={{
                        name: resumeFile?.name || resume?.name || 'resume',
                        url: resumePreviewUrl,
                    }}
                    onDownload={handleResumePreviewDownload}
                    showDownloadButton
                    numPages={resumePreviewPages}
                    onDocumentLoadSuccess={({ numPages: loadedPages }) => setResumePreviewPages(loadedPages)}
                />
            )}
        </>
    );
};

const styles = {
    modalContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '12px',
        background: 'white',
    },
    title: {
        fontFamily: 'Lemon milk pro',
        fontSize: 24,
        fontWeight: 600,
        textAlign: 'center',
        textWrap: 'nowrap',
    },
    subtitle: {
        width: '435px',
        marginTop: '8px',
        marginBottom: '24px',
        textAlign: 'center',
    },
    templateOptionImage: {
        width: '193.5px',
        height: '274.608px',
        borderRadius: '8px',
    },
    comingSoonTemplateImage: {
        filter: `brightness(70%) blur(1.8px)`,
        overflow: 'hidden',
    },
    selectedTemplateImage: {
        border: '2px solid #4239B0',
    },
    templatesContainer: {
        marginTop: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '45px',
    },
    downloadContainer: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '56px 100px',
    } as CSSProperties,
    closeButtonStyle: {
        width: '48px',
        height: '48px',
        padding: '8px',
        position: 'absolute',
        top: '23px',
        right: '30px',
        boxSizing: 'border-box',
        borderRadius: '24px',
    } as CSSProperties,
    actionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
    } as CSSProperties,
    actionButtonContainer: {
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'center',
        width: '100%',
    } as CSSProperties,
    purpleButton: {
        padding: '12px 24px',
        width: '194px',
    },
    secondaryButton: {
        width: '194px',
        padding: '12px 24px',
    },
    purpleButtonText: {
        fontSize: 16,
        fontWeight: 'bold',
        textWrap: 'nowrap',
    },
    initializeSetupContainer: {
        padding: '48px 65px 32px 65px',
        width: '751px',
        boxSizing: 'border-box',
        margin: '0 auto',
    } as CSSProperties,
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '131.5px 169.5px',
    } as CSSProperties,
    comingSoonText: {
        backgroundColor: 'grey',
        position: 'absolute',
        top: '243px',
        width: 'fit-content',
        padding: '6px 12px',
        height: '36px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        fontFamily: 'Plus Jakarta Sans',
        fontWeight: 700,
        lineHeight: '24px',
        borderRadius: '24px',
        background: '#CBCBCB',
        boxShadow: '0px 4px 80px 0px rgba(112, 118, 176, 0.33)',
        color: 'white',
    } as CSSProperties,
    pdfPreviewContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '24px',
    },
};

export default CvGenerationModal;
